import React, { useMemo, useState } from 'react';
import { TaskItem } from '../../types/tasks';
import { useTasks } from '../../context/TasksContext';
import { isCritical, isLate, isScheduled, rangeLabel } from '../../utils/projectPlan';
import { TaskValue, isMaterial, isRanked, money, num, valueOf } from '../../utils/projectValue';
import ProgressCheck from '../tasks/ProgressCheck';
import TaskIcon from '../tasks/TaskIcon';
import ItemActions from './ItemActions';

type Sort = 'perK' | 'perHr' | 'gain';

const SORTS: { value: Sort; label: string }[] = [
  { value: 'perK', label: 'Best per $' },
  { value: 'perHr', label: 'Best per hour' },
  { value: 'gain', label: 'Biggest gain' },
];

interface Ranked {
  item: TaskItem;
  v: TaskValue;
}

/** Higher first; tasks without that figure go last. */
const byMetric = (key: Sort) => (a: Ranked, b: Ranked) => {
  const x = key === 'gain' ? a.v.gain : a.v[key];
  const y = key === 'gain' ? b.v.gain : b.v[key];
  if (x === null && y === null) return 0;
  if (x === null) return 1;
  if (y === null) return -1;
  return y - x;
};

const Bar: React.FC<{ value: number | null; best: number; title: string }> = ({ value, best, title }) =>
  value === null ? (
    <span className="tp-v-bar is-empty" title="Needs a cost or hours">—</span>
  ) : (
    <span className="tp-v-bar" title={title}>
      <span style={{ width: `${best > 0 ? Math.max(4, (value / best) * 100) : 0}%` }} />
      <em>{num(value)}</em>
    </span>
  );

/**
 * Tasks ranked by what they're worth: the gain each brings (lap time,
 * weight…) per $1,000 it costs — its own cost, or the materials under it when it has none — and per hour
 * of work, side by side, sortable by either. Gains in different units can't
 * be compared, so each unit has its own table.
 */
const ValueView: React.FC<{ items: TaskItem[]; readOnly: boolean; onOpenItem: (id: number) => void }> = ({ items, readOnly, onOpenItem }) => {
  const { costs, updateItem } = useTasks();
  const [scope, setScope] = useState<'open' | 'all'>('open');
  const [sort, setSort] = useState<Sort>('perK');
  const parents = useMemo(() => new Set(items.map((i) => i.parent_id)), [items]);

  const { groups, unpriced } = useMemo(() => {
    const out = new Map<string, Ranked[]>();
    const missing: TaskItem[] = [];
    for (const item of items) {
      if (isMaterial(item)) continue;
      if (scope === 'open' && item.progress >= 100) continue;
      const v = valueOf(item, costs);
      if (!v) continue;
      if (!isRanked(v)) {
        missing.push(item);
        continue;
      }
      const key = v.unit.toLowerCase();
      out.set(key, [...(out.get(key) || []), { item, v }]);
    }
    return {
      groups: Array.from(out.values()).map((list) => list.sort(byMetric(sort))).sort((a, b) => b.length - a.length),
      unpriced: missing,
    };
  }, [items, costs, scope, sort]);

  const head = (key: Sort, label: string, className: string) => (
    <button type="button" className={`tp-v-col ${className}${sort === key ? ' is-sorted' : ''}`} onClick={() => setSort(key)} title={`Sort by ${label.toLowerCase()}`}>
      {label}
      {sort === key && <TaskIcon name="chevron-down" size={11} />}
    </button>
  );

  return (
    <div className="tp-valueview">
      <div className="tp-gantt-tools">
        <div className="tk-segmented tk-segmented-sm" role="group" aria-label="Show">
          <button type="button" className={scope === 'open' ? 'is-active' : ''} onClick={() => setScope('open')}>Still to do</button>
          <button type="button" className={scope === 'all' ? 'is-active' : ''} onClick={() => setScope('all')}>All</button>
        </div>
        <div className="tk-segmented tk-segmented-sm" role="group" aria-label="Sort by">
          {SORTS.map((s) => (
            <button key={s.value} type="button" className={sort === s.value ? 'is-active' : ''} onClick={() => setSort(s.value)}>{s.label}</button>
          ))}
        </div>
        <span className="tp-gantt-legend">Gain per $1,000 (its own cost, or what's under it when it has none) and per hour of work</span>
      </div>

      {!groups.length && !unpriced.length && (
        <div className="tp-empty">
          <TaskIcon name="value" size={28} />
          <p>
            No task has a value yet. Open a task's ⋯ › Task value and say what it gains — "0.3 s laptime", "4 lb" — and what it
            costs and/or how long it takes.
          </p>
        </div>
      )}

      {groups.map((list) => {
        const unit = list[0].v.unit;
        const bestK = Math.max(0, ...list.map((r) => r.v.perK ?? 0));
        const bestHr = Math.max(0, ...list.map((r) => r.v.perHr ?? 0));
        const open = list.filter((r) => r.item.progress < 100);
        const gain = open.reduce((t, r) => t + r.v.gain, 0);
        const spend = open.reduce((t, r) => t + r.v.cost, 0);
        const hours = open.reduce((t, r) => t + (r.v.hours ?? 0), 0);
        return (
          <section key={unit.toLowerCase()} className="tl-card tp-v-group">
            <header className="tp-v-head">
              <h3>{unit}</h3>
              <span className="tk-muted">
                Still to gain {num(gain)} {unit}
                {spend > 0 && ` · ${money(spend)}`}
                {hours > 0 && ` · ${num(hours)} hr`}
              </span>
            </header>
            <div className="tp-v-row tp-v-cols" aria-hidden="true">
              <span className="tp-v-rank" />
              <span className="tp-v-check" />
              <span className="tp-v-name tp-v-col">Task</span>
              {head('gain', 'Gain', 'tp-v-gain')}
              <span className="tp-v-col tp-v-basis">Cost</span>
              <span className="tp-v-col tp-v-hours">Hours</span>
              {head('perK', 'Per $1k', 'tp-v-metric')}
              {head('perHr', 'Per hour', 'tp-v-metric')}
              <span className="tp-v-actions-col" />
            </div>
            <ol className="tp-v-list">
              {list.map(({ item, v }, i) => {
                const done = item.progress >= 100;
                return (
                  <li key={item.ckey || item.id} className={`tp-v-row${done ? ' is-done' : ''}`}>
                    <span className="tp-v-rank">{i + 1}</span>
                    <span className="tp-v-check">
                      <ProgressCheck
                        value={item.progress}
                        onToggle={() => !readOnly && updateItem(item.id, { progress: done ? 0 : 100 })}
                        disabled={readOnly}
                        label={`${done ? 'Uncheck' : 'Check'} ${item.text}`}
                        size={18}
                      />
                    </span>
                    <button type="button" className="tp-v-name" onClick={() => onOpenItem(item.id)} title="Show it on the map">
                      {item.text || 'Task'}
                      {!done && isScheduled(item) && (
                        <small className={isLate(item) || isCritical(item) ? 'is-critical' : ''}>
                          {rangeLabel(item.plan_start!, item.plan_finish!)}
                          {isLate(item) ? ' · late' : ''}{isCritical(item) ? ' · critical' : ''}
                        </small>
                      )}
                    </button>
                    <span className="tp-v-gain">+{num(v.gain)}</span>
                    <span className="tp-v-basis">{v.cost > 0 ? money(v.cost) : '—'}</span>
                    <span className="tp-v-hours">{v.hours ? `${num(v.hours)} hr` : '—'}</span>
                    <Bar value={v.perK} best={bestK} title={v.perK !== null ? `${num(v.perK)} ${unit} per $1,000` : ''} />
                    <Bar value={v.perHr} best={bestHr} title={v.perHr !== null ? `${num(v.perHr)} ${unit} per hour` : ''} />
                    <ItemActions item={item} readOnly={readOnly} hasChildren={parents.has(item.id)} onAddChild={() => undefined} maxPeople={1} canAddChild={false} />
                  </li>
                );
              })}
            </ol>
          </section>
        );
      })}

      {unpriced.length > 0 && (
        <section className="tp-v-missing">
          <h3 className="tk-section-label">Needs a cost or hours to rank</h3>
          <ul>
            {unpriced.map((item) => (
              <li key={item.ckey || item.id}>
                <button type="button" className="tp-v-name" onClick={() => onOpenItem(item.id)}>{item.text || 'Task'}</button>
                <span className="tk-muted">+{num(item.benefit!)} {item.benefit_unit}</span>
                <ItemActions item={item} readOnly={readOnly} hasChildren={parents.has(item.id)} onAddChild={() => undefined} maxPeople={1} canAddChild={false} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default ValueView;
